
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';


import { DesignLayer, TransformersLayer } from '../../components/Layers';
import { AppProviderOverridenValue } from '../../context';
import { SET_CANVAS_SIZE } from '../../actions';
import { useResizeObserver, useStore } from '../../hooks';
import NodeControls from '../../components/NodeControls';
import CanvasNode from './CanvasNode';
import CanvasModeToolbar from './CanvasModeToolbar';
import { CanvasContainer, StyledOrignalImage } from './MainCanvas.styled';

const MIN_CANVAS_DIMENSION = 1;

const readContainerSize = (element) => {
  if (!element) {
    return { width: 0, height: 0 };
  }
  const { width, height } = element.getBoundingClientRect();
  return {
    width: Math.round(width),
    height: Math.round(height),
  };
};

const MainCanvas = () => {
  const [observeResize, unobserveResize] = useResizeObserver();
  const providedAppContext = useStore();
  const { dispatch, originalImage, textIdOfEditableContent, isShowOriginalImage } =
    providedAppContext;
  const containerRef = useRef(null);

  const applyCanvasSize = useCallback(
    (width, height) => {
      if (width < MIN_CANVAS_DIMENSION || height < MIN_CANVAS_DIMENSION) {
        return;
      }
      dispatch({
        type: SET_CANVAS_SIZE,
        payload: {
          canvasWidth: width,
          canvasHeight: height,
        },
      });
    },
    [dispatch],
  );

  const measureContainer = useCallback(() => {
    const { width, height } = readContainerSize(containerRef.current);
    applyCanvasSize(width, height);
  }, [applyCanvasSize]);

  useLayoutEffect(() => {
    measureContainer();
    const raf = requestAnimationFrame(measureContainer);
    return () => cancelAnimationFrame(raf);
  }, [measureContainer]);

  // After template remount the image often loads after the first (zero) measure pass.
  useEffect(() => {
    if (!providedAppContext.originalImage?.width) {
      return undefined;
    }
    measureContainer();
    const raf = requestAnimationFrame(measureContainer);
    return () => cancelAnimationFrame(raf);
  }, [originalImage, measureContainer]);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return undefined;
    }

    observeResize(element, ({ width, height }) => {
      if (width >= MIN_CANVAS_DIMENSION && height >= MIN_CANVAS_DIMENSION) {
        applyCanvasSize(Math.round(width), Math.round(height));
        return;
      }
      measureContainer();
    });

    measureContainer();

    return () => {
      unobserveResize(element);
    };
  }, [observeResize, unobserveResize, applyCanvasSize, measureContainer]);

  return (
    <CanvasContainer
      className="respark_canvas-container"
      ref={containerRef}
    >
      {!textIdOfEditableContent && <NodeControls />}
      {isShowOriginalImage && originalImage?.src && (
        <StyledOrignalImage
          className="respark_original-image-compare"
          src={originalImage.src}
        />
      )}
      <CanvasNode>
        <AppProviderOverridenValue overridingValue={providedAppContext}>
          <DesignLayer />
          <TransformersLayer />
        </AppProviderOverridenValue>
      </CanvasNode>
      <CanvasModeToolbar />
    </CanvasContainer>
  );
};

export default MainCanvas;
